import { useDispatch } from 'react-redux';
import { addToCart } from '../../store/cartSlice';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import { showWarning } from '../../utils/sweetAlert';
import { getStockIssueMessage, getStockLimit, validateCartStock } from '../../utils/stockValidation';

const ProductCard = ({ product }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  const basePrice = product.basePrice || product.price;

  const finalPrice = product.discount > 0
    ? parseFloat((basePrice - basePrice * product.discount / 100).toFixed(2))
    : basePrice;

  const getMinVariantPrice = () => {
    if (!product.hasVariants || !product.variants?.length) return finalPrice;
    const variantPrices = product.variants
      .filter(v => v.isActive !== false)
      .map(v => {
        const variantPrice = v.price || basePrice;
        return product.discount > 0
          ? variantPrice - (variantPrice * product.discount / 100)
          : variantPrice;
      });
    return variantPrices.length > 0 ? Math.min(...variantPrices) : finalPrice;
  };

  const minPrice = getMinVariantPrice();
  const hasMultiplePrices = product.hasVariants && product.variants?.some(v => v.price && v.price !== basePrice);
  const isOutOfStock = product.hasVariants
    ? product.variants?.filter((variant) => variant.isActive !== false).every((variant) => typeof variant.stock === 'number' && variant.stock <= 0)
    : typeof product.stock === 'number' && product.stock <= 0;

  const handleAddToCart = async () => {
    if (isOutOfStock) {
      showWarning(`${product.name} está agotado momentáneamente.`, 'Sin stock disponible');
      return;
    }

    if (product.hasVariants && product.variants?.length > 0) {
      navigate(`/product/${product._id}`);
      return;
    }

    try {
      const cartItem = { ...product, price: finalPrice, quantity: 1 };
      const result = await validateCartStock([cartItem]);
      if (!result.valid) {
        showWarning(getStockIssueMessage(result.issues), 'Sin stock disponible');
        return;
      }

      dispatch(addToCart({
        ...cartItem,
        stockLimit: getStockLimit(result, product._id)
      }));
    } catch (error) {
      showWarning('No pudimos verificar el stock. Intentá nuevamente en unos segundos.', 'Stock no disponible');
    }
  };

  const handleViewDetails = () => {
    navigate(`/product/${product._id}`);
  };

  const getAllImages = () => {
    if (product.images?.length > 0) return product.images;
    if (product.image) return [product.image];
    return ['/placeholder-image.svg'];
  };

  const allImages = getAllImages();
  const hasMultipleImages = allImages.length > 1;

  const currentImageUrl = getDisplayImageUrl(allImages[currentImageIndex]);

  // Precargar primera imagen inmediatamente
  useEffect(() => {
    const img = new Image();
    img.src = getDisplayImageUrl(allImages[0]);
    img.onload = () => {
      if (currentImageIndex === 0) {
        setImageLoading(false);
      }
    };
  }, []);

  useEffect(() => {
    let interval;
    if (isHovering && hasMultipleImages) {
      interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
      }, 1200);
    } else {
      setCurrentImageIndex(0);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [isHovering, hasMultipleImages, allImages.length]);

  useEffect(() => {
    // Solo mostrar loading al cambiar de imagen (no en la primera carga)
    if (currentImageIndex !== 0) {
      setImageLoading(true);
    }
  }, [currentImageIndex]);

  return (
    <div className="product-card" style={{ cursor: 'pointer' }}>
      {/* Image */}
      <div
        className="position-relative product-image-container"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onClick={handleViewDetails}
        style={{
          overflow: 'hidden',
          aspectRatio: '3/4',
          background: 'var(--bg-secondary)',
          position: 'relative'
        }}
      >
        {imageLoading && (
          <div className="image-loader">
            <div className="image-spinner"></div>
          </div>
        )}
        <img
          src={currentImageUrl}
          alt={product.name}
          loading="eager"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: imageLoading ? 0 : 1,
            transition: 'opacity 0.3s ease, transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
            display: 'block',
            transform: isHovering ? 'scale(1.04)' : 'scale(1)'
          }}
          onLoad={() => setImageLoading(false)}
          onError={(e) => { e.target.src = '/placeholder-image.svg'; setImageLoading(false); }}
        />

        {/* Quick Add Button — appears on hover */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '12px',
          opacity: isHovering ? 1 : 0,
          transform: isHovering ? 'translateY(0)' : 'translateY(10px)',
          transition: 'all 0.3s ease',
          display: 'flex',
          justifyContent: 'center',
          zIndex: 3
        }}>
          <button
            onClick={(e) => { e.stopPropagation(); handleAddToCart(); }}
            disabled={isOutOfStock}
            style={{
              width: '100%',
              padding: '10px',
              background: 'var(--brand-primary)',
              color: '#fff',
              border: 'none',
              fontSize: '0.7rem',
              fontWeight: 500,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
              backdropFilter: 'blur(10px)',
              transition: 'background 0.2s ease',
              opacity: 0.92
            }}
            onMouseEnter={e => e.target.style.background = 'var(--brand-secondary)'}
            onMouseLeave={e => e.target.style.background = 'var(--brand-primary)'}
          >
            {isOutOfStock ? 'Agotado' : product.hasVariants ? 'Ver Opciones' : 'Agregar al Carrito'}
          </button>
        </div>

        {/* Discount Badge */}
        {product.discount > 0 && (
          <div style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            background: 'var(--brand-primary)',
            color: '#fff',
            padding: '4px 10px',
            fontSize: '0.65rem',
            fontWeight: 600,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            zIndex: 3
          }}>
            -{product.discount}%
          </div>
        )}

      </div>

      {/* Product Info */}
      <div onClick={handleViewDetails} style={{ padding: '16px 0 0' }}>
        <h2 style={{
          fontFamily: 'var(--font-body)',
          fontSize: '0.95rem',
          fontWeight: 400,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--text-primary)',
          margin: '0 0 8px',
          lineHeight: 1.4
        }}>
          {product.name}
        </h2>

        {/* Variants count */}
        {product.hasVariants && product.variants?.length > 0 && (
          <p style={{
            color: 'var(--text-light)',
            fontSize: '0.85rem',
            margin: '0 0 6px',
            letterSpacing: '0.03em'
          }}>
            {product.variants.length} variante{product.variants.length > 1 ? 's' : ''}
          </p>
        )}

        {/* Price */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {product.discount > 0 && (
            <span style={{
              color: 'var(--text-light)',
              fontSize: '0.9rem',
              textDecoration: 'line-through'
            }}>
              ${basePrice}
            </span>
          )}
          <span style={{
            color: product.discount > 0 ? 'var(--brand-primary)' : 'var(--text-primary)',
            fontSize: '1rem',
            fontWeight: 500
          }}>
            {hasMultiplePrices
              ? `Desde $${minPrice.toFixed(2)}`
              : `$${product.discount > 0 ? finalPrice : basePrice}`
            }
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
